import type {
  FeaturesResponse,
  LivenessResponse,
  MatrixValidationResponse,
  ProblemDetails,
  ProviderInfo,
  ProvidersListResponse,
  ReadinessResponse,
  ValidationResult,
} from "./types";

const getBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || "http://localhost:5024";
};

// Telemetry callback for RTT measurements
type TelemetryCallback = (rtt: number) => void;
let telemetryCallback: TelemetryCallback | null = null;

export const setTelemetryCallback = (
  callback: TelemetryCallback | null,
): void => {
  telemetryCallback = callback;
};

interface FetchWithRttOptions {
  reportRtt?: boolean;
}

// Wrapper for fetch that measures RTT
const fetchWithRtt = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: FetchWithRttOptions,
): Promise<Response> => {
  const startTime = performance.now();
  try {
    const response = await fetch(input, init);
    const endTime = performance.now();
    const rtt = endTime - startTime;

    // Report RTT only for validation calls.
    if (options?.reportRtt && telemetryCallback) {
      telemetryCallback(rtt);
    }

    return response;
  } catch (error) {
    // Still measure RTT even on error
    const endTime = performance.now();
    const rtt = endTime - startTime;
    if (options?.reportRtt && telemetryCallback) {
      telemetryCallback(rtt);
    }
    throw error;
  }
};

class ApiError extends Error {
  constructor(
    public status: number,
    public problemDetails: ProblemDetails,
  ) {
    super(problemDetails.detail || problemDetails.title);
    this.name = "ApiError";
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    let problemDetails: ProblemDetails;
    try {
      problemDetails = await response.json();
    } catch {
      problemDetails = {
        title: "An error occurred",
        status: response.status,
        detail: response.statusText,
      };
    }
    throw new ApiError(response.status, problemDetails);
  }
  return response.json();
};

export const apiClient = {
  // Validation endpoints
  validateEmail: async (email: string): Promise<ValidationResult> => {
    const response = await fetchWithRtt(
      `${getBaseUrl()}/api/v1/validate?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
      { reportRtt: true },
    );
    return handleResponse<ValidationResult>(response);
  },

  validateEmailMatrix: async (
    email: string,
  ): Promise<MatrixValidationResponse> => {
    const response = await fetchWithRtt(
      `${getBaseUrl()}/api/v1/validate/matrix?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
      { reportRtt: true },
    );
    return handleResponse<MatrixValidationResponse>(response);
  },

  // Provider endpoints
  getProviders: async (): Promise<ProviderInfo[]> => {
    const response = await fetchWithRtt(`${getBaseUrl()}/api/v1/providers`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    const result = await handleResponse<ProvidersListResponse>(response);
    return result.providers;
  },

  getFeatures: async (): Promise<FeaturesResponse> => {
    const response = await fetchWithRtt(`${getBaseUrl()}/api/v1/features`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    return handleResponse<FeaturesResponse>(response);
  },

  // Health endpoints
  checkReadiness: async (): Promise<ReadinessResponse> => {
    const response = await fetchWithRtt(`${getBaseUrl()}/readyz`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    return handleResponse<ReadinessResponse>(response);
  },

  checkLiveness: async (): Promise<LivenessResponse> => {
    const response = await fetchWithRtt(`${getBaseUrl()}/livez`, {
      method: "GET",
      headers: {
        Accept: "text/plain",
      },
    });
    if (!response.ok) {
      throw new ApiError(response.status, {
        title: "Liveness check failed",
        status: response.status,
        detail: response.statusText,
      });
    }
    const text = await response.text();
    // Convert plain text 'ok' to LivenessResponse format for compatibility
    return { status: text === "ok" ? "alive" : text };
  },
};

export { ApiError };
